import { createSelector } from 'reselect'
import { spotIntersect } from '@calendly/booking-kit'

import { eventTypeSelector } from 'root-api'
import {
  selectedDayCalendarSelector,
  spotsSelector
} from 'selectors'
import {
  spotTimeForConflictsSelector,
  daySelector
} from 'selectors/calendar'
import { calendarSpotsSelector, calendarInviteeEventsSelector } from 'selectors/calendars'
import { currentUserSelector } from 'selectors/user'
import { isEmbeddedSelector } from 'selectors/embed'
import { isDiagnosticsEnabledSelector } from 'selectors/diagnostics'


const isEtMemberSelector = createSelector(
  eventTypeSelector,
  (eventType) => eventType.policy?.member
)

export const isMutualAvailabilityEnabledSelector = createSelector(
  isEtMemberSelector,
  isDiagnosticsEnabledSelector,
  isEmbeddedSelector,
  currentUserSelector,
  (isMember, isDiagnosticsEnabled, isEmbedded, currentUser) => (
    Boolean(currentUser)
      && !isMember
      && !isDiagnosticsEnabled
      && !isEmbedded
  )
)

export const isMutualAvailabilityLoginEnabledSelector = createSelector(
  isEmbeddedSelector,
  currentUserSelector,
  (isEmbedded, currentUser) => (
    !currentUser && !isEmbedded
  )
)

export const inviteeEventsSelector = createSelector(
  daySelector,
  selectedDayCalendarSelector,
  (day, calendar) => {
    if (!calendar) return null
    const dayData = calendar.days.find((calendarDay) => calendarDay.date === day.id)
    if (!dayData) return null
    return dayData.invitee_events
  }
)

const inviteeEventSelector = createSelector(
  inviteeEventsSelector,
  (_, { id }) => id,
  (inviteeEventList, id) => inviteeEventList.find((event) => id === event.id)
)

export const inviteeEventHasConflictSelector = createSelector(
  inviteeEventSelector,
  eventTypeSelector,
  spotTimeForConflictsSelector,
  (
    {
      start_time: startTime,
      end_time: endTime
    }, { duration }, spotTimeForConflicts
  ) => spotIntersect(spotTimeForConflicts, duration, startTime, endTime)
)

export const firstEventWithConflictIdSelector = createSelector(
  inviteeEventsSelector,
  eventTypeSelector,
  spotTimeForConflictsSelector,
  (inviteeEventList, { duration }, spotTimeForConflicts) => {
    if (!inviteeEventList) return null
    const eventWithConflict = inviteeEventList.find(
      (event) => !event.all_day && spotIntersect(spotTimeForConflicts, duration, event.start_time, event.end_time)
    )
    return eventWithConflict ? eventWithConflict.id : null
  }
)

export const spotsWithConflictsSelector = createSelector(
  spotsSelector,
  inviteeEventsSelector,
  eventTypeSelector,
  (spots, inviteeEvents, { duration }) => {
    const startTimes = new Set()
    spots.forEach(({ start_time: startTime }) => {
      const hasConflict = inviteeEvents.some(
        (item) => spotIntersect(startTime, duration, item.start_time, item.end_time)
      )
      if (hasConflict) startTimes.add(startTime)
    })
    return startTimes
  }
)

export const calendarSpotsWithConflictsSelector = createSelector(
  calendarSpotsSelector,
  calendarInviteeEventsSelector,
  eventTypeSelector,
  (spots, inviteeEvents, { duration }) => {
    const startTimes = new Set()
    spots.forEach(({ start_time: startTime }) => {
      const hasConflict = inviteeEvents.some(
        (item) => spotIntersect(startTime, duration, item.start_time, item.end_time)
      )
      if (hasConflict) startTimes.add(startTime)
    })
    return startTimes
  }
)

export const calendarSpotsWithoutConflictsSelector = createSelector(
  calendarSpotsSelector,
  calendarSpotsWithConflictsSelector,
  (spots, spotsWithConflicts) => {
    const availableSpots = []
    spots.forEach((spot) => {
      const hasConflict = Array.from(spotsWithConflicts).includes(spot.start_time)
      if (!hasConflict) availableSpots.push(spot)
    })
    return availableSpots
  }
)
